import type { StyleSpecification } from 'maplibre-gl'
import { MAP_LABEL_FONT } from './mapConfig'
import { offsetMeters } from './walkGeo'

/** Which map assets a turf needs to be usable offline (spec §4.3): tiles around its stops + style assets. */

export type Bounds = { west: number; south: number; east: number; north: number }

export const OFFLINE_MARGIN_M = 250
export const OFFLINE_ZOOMS = [12, 13, 14, 15] as const

/** Bounding box of the located stops; null when none has coordinates. */
export function stopsBounds(stops: { lat: number | null; lng: number | null }[]): Bounds | null {
  let b: Bounds | null = null
  for (const s of stops) {
    if (s.lat == null || s.lng == null) continue
    b = b
      ? {
          west: Math.min(b.west, s.lng), east: Math.max(b.east, s.lng),
          south: Math.min(b.south, s.lat), north: Math.max(b.north, s.lat),
        }
      : { west: s.lng, east: s.lng, south: s.lat, north: s.lat }
  }
  return b
}

/** Grow each side by `meters`. */
export function expandBounds(b: Bounds, meters: number): Bounds {
  const [west, south] = offsetMeters([b.west, b.south], -meters, -meters)
  const [east, north] = offsetMeters([b.east, b.north], meters, meters)
  return { west, south, east, north }
}

const MAX_LAT = 85.0511287798

function tileX(lng: number, z: number): number {
  const n = 2 ** z
  return Math.min(n - 1, Math.max(0, Math.floor(((lng + 180) / 360) * n)))
}

function tileY(lat: number, z: number): number {
  const n = 2 ** z
  const clamped = Math.min(MAX_LAT, Math.max(-MAX_LAT, lat))
  const r = (clamped * Math.PI) / 180
  const y = ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n
  return Math.min(n - 1, Math.max(0, Math.floor(y)))
}

/** How many tiles `tileKeysForBounds` would return, without building the keys (cheap on a huge box). */
export function tileCountForBounds(b: Bounds, zooms: readonly number[]): number {
  let n = 0
  for (const z of zooms) n += (tileX(b.east, z) - tileX(b.west, z) + 1) * (tileY(b.south, z) - tileY(b.north, z) + 1)
  return n
}

/** Slippy-map (XYZ) tile keys `tile/z/x/y` covering the bounds at each zoom. */
export function tileKeysForBounds(b: Bounds, zooms: readonly number[]): string[] {
  const keys: string[] = []
  for (const z of zooms) {
    const x0 = tileX(b.west, z)
    const x1 = tileX(b.east, z)
    const y0 = tileY(b.north, z) // north = smaller y
    const y1 = tileY(b.south, z)
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) keys.push(`tile/${z}/${x}/${y}`)
  }
  return keys
}

/** Glyph ranges + sprite files the style needs: every text-font used (plus the label font) and the sprite. */
export function styleAssetKeys(style: StyleSpecification): string[] {
  const stacks = new Set<string>([MAP_LABEL_FONT.join(',')])
  for (const layer of style.layers ?? []) {
    const font = (layer as { layout?: Record<string, unknown> }).layout?.['text-font']
    if (Array.isArray(font) && font.length > 0 && font.every((f) => typeof f === 'string')) {
      stacks.add((font as string[]).join(','))
    }
  }
  const keys = [...stacks].map((s) => `glyph/${s}/0-255`)
  keys.push('sprite/light.json', 'sprite/light.png', 'sprite/light@2x.json', 'sprite/light@2x.png')
  return keys
}
